import { Invoices_AdvanceDetail, Invoices_PaidAdvance } from "data/endpoints";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import { getRouteParams } from "features/shared/helpers/routeParams";
import React, { useState } from "react";
import AdvanceDTO from "../dtos/AdvanceDTO";
import PageCard from "features/shared/components/card/PageCard";
import Advance from "../components/Advance";
import AdvanceHeader from "../components/AdvanceHeader";
import InvoiceTotalAmount from "../components/InvoiceTotalAmount";
import InvoiceDTO from "../dtos/InvoiceDTO";
import { InvoiceRecapitulationType } from "../enums/invoiceRecapitulationType";
import { InvoiceType } from "../enums/invoiceType";
import InvoiceGridPagination from "../components/InvoiceGridPagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DataFilterModal from "features/shared/components/dataFilterModal/DataFilterModal";
import { useQuery } from "data/hooks/useQuery";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { isEmpty } from "features/shared/helpers/objects";
import { loadDataFilterConfig } from "features/shared/helpers/modules";
import CardHeaderButton from "features/shared/components/card/CardHeaderButton";
import { useStateDataGrid } from "features/shared/components/dataGrid/hooks/useStateDataGrid";
import { useTranslation } from "react-i18next";

const advancesDataFilterJson = loadDataFilterConfig("invoiceDetailAdvancesDataFilter");
const pageSize = 5;

interface Props {
  invoice: InvoiceDTO;
  proformaInvoiceId?: string;
}

const AdvancesSection = ({ invoice, proformaInvoiceId }: Props) => {
  // translations
  const tLabels = useTranslation("invoice\\invoice").t;

  // state
  const [wasOpenedFilter, setWasOpenedFilter] = useState(false);

  // data grid hook
  const {
    dataGrid,
    descriptors,
    setDescriptors,
    showFilter,
    setShowFilter,
    paginationPageChangeHandler,
    filterApplyHandler,
    filterResetHandler
  } = useStateDataGrid(pageSize, advancesDataFilterJson);

  // fetch-data
  const getEndpointUrl = (): string => {
    switch (invoice.invoiceType) {
      case InvoiceType.Invoice:
        return Invoices_PaidAdvance;
      case InvoiceType.Proformainvoice:
        return Invoices_AdvanceDetail;
      default:
        return "";
    }
  };

  const canLoadAdvances = (): boolean => {
    return !isEmpty(invoice) && dataGrid.computedUrlSearchParams !== "";
  };

  const getSearchParams = (): URLSearchParams => { 
    let params = new URLSearchParams(dataGrid.computedUrlSearchParams)
    if (proformaInvoiceId) {
      params.set("ProformaInvoiceId", proformaInvoiceId)
    }
    
    return params;
  }

  const { data: advances, loadStatus: advancesStatus } = useQuery<
    PagedDataResultDTO<AdvanceDTO>
  >(
    {
      url: getEndpointUrl(),
      routeParams: getRouteParams("invoiceId", invoice.invoiceId!),
      params: getSearchParams(),
    },
    {
      result: [],
      count: 0,
    },
    [invoice, dataGrid.computedUrlSearchParams, proformaInvoiceId],
    undefined,
    {
      apply: true,
      condition: canLoadAdvances,
    }
  );

  // helpers
  const getTitle = (): string => {
    switch (invoice.invoiceType) {
      case InvoiceType.Invoice:
        return tLabels("advancesSection.paidAdvances");
      case InvoiceType.Proformainvoice:
        return tLabels("advancesSection.detailAdvanceInvoice");
      default:
        return "";
    }
  };

  const areZeroAdvances = () => {
    let recapitulationItem = invoice.invoiceRecapitulation?.find(
      (x) =>
        x.invoiceRecapitulationType == InvoiceRecapitulationType.PaidAdvance
    );

    if (recapitulationItem && recapitulationItem.taxInclusiveAmount === 0) {
      return true;
    }

    return false;
  };

  // handlers
  const openFilterHandler = () => {
    setShowFilter(true);
    setWasOpenedFilter(true);
  };

  // sub-renders
  const renderTotalAmount = () => {
    if (invoice.invoiceType == InvoiceType.Invoice) {
      return (
        <InvoiceTotalAmount
          invoiceRecapitulation={invoice.invoiceRecapitulation!}
          recapitulationType={InvoiceRecapitulationType.PaidAdvance}
          currency={invoice.currency!}
          includeTax
        />
      );
    } else if (invoice.invoiceType == InvoiceType.Proformainvoice) {
      return (
        <InvoiceTotalAmount
          invoiceRecapitulation={invoice.invoiceRecapitulation!}
          recapitulationType={InvoiceRecapitulationType.Total}
          currency={invoice.currency!}
          includeTax
        />
      );
    }
  };

  const renderActionBtns = () => {
    let result: JSX.Element[] = [];

    if (advances.count > 1 || wasOpenedFilter) {
      let elem = (
        <CardHeaderButton
          type="button"
          onClick={openFilterHandler}
          key={invoice.invoiceId}
        >
          <FontAwesomeIcon icon="filter" />
        </CardHeaderButton>
      );
      result.push(elem);
    }

    return result;
  };

  // render
  if (!areZeroAdvances()) {
    return (
      <PageCard title={getTitle()} headerActionBtns={renderActionBtns}>
        <NoContentWrapper data={advances} loadStatus={advancesStatus}>
          <AdvanceHeader />

          {advances.result.map((item, index) => (
            <div className="py-1 py-sm-2" key={index}>
              <Advance advance={item} />
            </div>
          ))}

          <div className="d-flex flex-column flex-sm-row align-items-center mt-2 mb-1 pt-3 border-top">
            <InvoiceGridPagination
              urlParams={
                new URLSearchParams(dataGrid.paginationUrlSearchParams)
              }
              count={advances.count}
              pageSize={pageSize}
              onPageChange={paginationPageChangeHandler}
            />
            {renderTotalAmount()}
          </div>
        </NoContentWrapper>

        {(advances.count > 1 || wasOpenedFilter) && (
          <DataFilterModal
            descriptors={descriptors}
            setDescriptors={setDescriptors}
            applyButtonOnClick={filterApplyHandler}
            resetButtonOnClick={filterResetHandler}
            show={showFilter}
            setShow={setShowFilter}
          />
        )}
      </PageCard>
    );
  } else {
    return null;
  }
};

export default AdvancesSection;
